/**
 * V17.523 (Phase 1, Rules 8–9, B7): the pinned footer — avatar, name, active role and "+N" when the
 * user holds more roles (switching is the top slot's SidebarRoleControl since V17.539), ລາວ/EN, dark
 * mode, sign out. On the phone, sign out asks first: "ອອກຈາກລະບົບບໍ?" / "Sign out?" (B7).
 */
import { useState } from 'react';
import { LogOut, Moon, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { NAV_STRINGS } from './navStrings';

interface SidebarFooterProps {
  /** Phone drawer: 44 px controls and a confirm before sign out. */
  touch?: boolean;
  /** Collapsed desktop rail: icons only, stacked. */
  rail?: boolean;
}

export function SidebarFooter({ touch, rail }: SidebarFooterProps) {
  const { user, logout, availableRoles, activeRoleName } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const s = NAV_STRINGS[language];

  const name = user?.full_name || user?.username || '';
  const role = activeRoleName || user?.role_name || '';
  const extraRoles = Math.max(0, (availableRoles?.length ?? 0) - 1);
  const initial = (name.trim()[0] || '?').toUpperCase();
  const ctl = touch ? 'min-h-[44px] min-w-[44px]' : 'min-h-[36px] min-w-[36px]';

  const signOut = () => {
    logout();
    navigate('/login');
  };

  const avatar = (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sidebar-active-bg text-sm font-semibold text-sidebar-active-fg"
      aria-hidden="true"
    >
      {initial}
    </span>
  );

  if (rail) {
    return (
      <div className="flex flex-col items-center gap-2 border-t border-sidebar-border py-3" data-testid="sidebar-footer">
        <span title={`${name} · ${role}`}>{avatar}</span>
        <button type="button" onClick={toggleTheme} aria-label={isDark ? s.light : s.dark}
          className={cn(ctl, 'flex items-center justify-center rounded-lg text-sidebar-icon hover:bg-sidebar-hover')}>
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
        <button type="button" onClick={signOut} aria-label={s.signOut}
          className={cn(ctl, 'flex items-center justify-center rounded-lg text-sidebar-danger hover:bg-sidebar-hover')}>
          <LogOut className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t border-sidebar-border p-3" data-testid="sidebar-footer">
      <div className="flex items-center gap-3 rounded-lg bg-sidebar-foot p-2" aria-label={s.userMenu}>
        {avatar}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-sidebar-strong">{name}</p>
          <p className="flex items-center gap-1.5 text-xs text-sidebar-muted">
            <span className="line-clamp-2">{role}</span>
            {extraRoles > 0 && (
              <span className="shrink-0 rounded-full bg-sidebar-chip px-1.5 text-sidebar-chip-fg" data-testid="extra-roles">
                {s.more(extraRoles)}
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <div className="flex overflow-hidden rounded-lg border border-sidebar-border" role="group" aria-label={s.lang}>
          {(['lo', 'en'] as const).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLanguage(l)}
              aria-pressed={language === l}
              className={cn(ctl, 'px-2 text-xs font-medium',
                language === l ? 'bg-sidebar-active-bg text-sidebar-active-fg' : 'text-sidebar-text hover:bg-sidebar-hover')}
            >
              {l === 'lo' ? 'ລາວ' : 'EN'}
            </button>
          ))}
        </div>
        <button type="button" onClick={toggleTheme} aria-label={isDark ? s.light : s.dark} title={isDark ? s.light : s.dark}
          className={cn(ctl, 'flex items-center justify-center rounded-lg text-sidebar-icon hover:bg-sidebar-hover')}>
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
        <button
          type="button"
          onClick={touch ? () => setConfirmOpen(true) : signOut}
          className={cn(ctl, 'ml-auto flex items-center gap-2 rounded-lg px-2 text-sm font-medium text-sidebar-danger hover:bg-sidebar-hover')}
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <span>{s.signOut}</span>
        </button>
      </div>

      {touch && (
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent closeLabel={s.close} className="top-1/3 w-[min(20rem,calc(100vw-2rem))] p-5 font-nav" lang={language}>
            <DialogTitle className="text-base font-semibold text-sidebar-strong">{s.signOutAsk}</DialogTitle>
            <DialogDescription className="sr-only">{s.signOutAsk}</DialogDescription>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmOpen(false)}
                className="min-h-[44px] rounded-lg px-4 text-sm font-medium text-sidebar-text hover:bg-sidebar-hover">
                {s.cancel}
              </button>
              <button type="button" onClick={() => { setConfirmOpen(false); signOut(); }}
                className="min-h-[44px] rounded-lg bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700">
                {s.yes}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
